/**
 * Notification store — Zustand store for rejection notifications.
 *
 * Phase 9 / Plan 09-04 — tracks rejected jobs as `RejectionNotice` objects.
 * The useRejectionPoller hook adds to this store when a job transitions to REJECTED.
 * The NotificationBell component reads from this store to show the unread count.
 *
 * Spec: UI-SPEC §7 (rejection toast + bell); DISPATCH-05.
 */

import { create } from 'zustand';

/** A single rejection notification record. */
export interface RejectionNotice {
  /** Job ID (UUID). */
  readonly jobId: string;
  /** Human-readable haul identifier (e.g. "H-20260613-001"). */
  readonly haulId: string;
  /** Full name of the driver who rejected the job. */
  readonly driverName: string;
  /** Rejection reason text (from job event metadata). */
  readonly reason: string;
  /** ISO timestamp when the rejection was detected by the poller. */
  readonly at: string;
}

interface NotificationState {
  /** All rejection notices in chronological order (newest last). */
  readonly rejections: readonly RejectionNotice[];
  /** Count of notifications not yet acknowledged by the dispatcher. */
  readonly unreadCount: number;
}

interface NotificationActions {
  /**
   * Record a new rejection notice. Increments `unreadCount` and appends to `rejections`.
   * Called by `useRejectionPoller` when a PENDING→REJECTED transition is detected.
   */
  readonly addRejection: (job: {
    id: string;
    haulId: string;
    driverName: string;
    /** Reason text — may be empty string if the driver provided no reason. */
    reason: string;
  }) => void;
  /** Mark all rejections as read. Resets `unreadCount` to 0. */
  readonly markAllRead: () => void;
}

type NotificationStore = NotificationState & NotificationActions;

export const useNotificationStore = create<NotificationStore>((set) => ({
  rejections: [],
  unreadCount: 0,

  addRejection: (job): void => {
    const notice: RejectionNotice = {
      jobId: job.id,
      haulId: job.haulId,
      driverName: job.driverName,
      reason: job.reason,
      at: new Date().toISOString(),
    };
    set((state) => ({
      rejections: [...state.rejections, notice],
      unreadCount: state.unreadCount + 1,
    }));
  },

  markAllRead: (): void => {
    set({ unreadCount: 0 });
  },
}));
